import { useState, useEffect } from "react";
import { useSearch } from "@tanstack/react-router";
import { Plus, Loader2, ListTodo, MapPin, Clock, Play, Download } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SCAN_REQUEST_STATUSES } from "@/types/domain";
import type { ScanRequestStatus } from "@/types/domain";
import { isLocalDev } from "@/lib/is-local-dev";
import { ScanRequestForm } from "./components/ScanRequestForm";
import { RunScanRequestDialog } from "./components/RunScanRequestDialog";

const STATUS_BADGE_STYLES: Record<ScanRequestStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  approved: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  running: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
  completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
};

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getVertexCount(polygon: unknown): number {
  if (!polygon || typeof polygon !== "object") return 0;
  const p = polygon as { coordinates?: number[][][] };
  const ring = p.coordinates?.[0];
  if (!Array.isArray(ring)) return 0;
  // GeoJSON rings repeat first vertex at end
  return ring.length > 0 ? ring.length - 1 : 0;
}

export function ScanRequestsPage() {
  const searchParams = useSearch({ from: "/dashboard/scan-requests" });
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    if (searchParams.new) {
      setShowForm(true);
    }
  }, [searchParams.new]);
  const [runDialogRequest, setRunDialogRequest] = useState<{
    id: string;
    name: string | null;
    polygon: unknown;
    requestedVariables: Record<string, unknown> | null;
  } | null>(null);

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.scanRequests.list.useQuery({
    status: statusFilter === "all" ? undefined : statusFilter,
    limit: 20,
  });

  const updateStatusMutation = trpc.scanRequests.updateStatus.useMutation({
    onSuccess: () => {
      utils.scanRequests.list.invalidate();
      toast.success("Status updated");
    },
    onError: (err) => toast.error(err.message),
  });

  const pullFromProdMutation = trpc.scanRequests.pullFromProd.useMutation({
    onSuccess: (data) => {
      utils.scanRequests.list.invalidate();
      toast.success(`Pulled ${data.count} request${data.count !== 1 ? "s" : ""} from production`);
    },
    onError: (err) => toast.error(err.message),
  });

  if (showForm) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <ScanRequestForm
          onClose={() => setShowForm(false)}
        />
      </div>
    );
  }

  const requests = data?.requests ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Scan Requests</h1>
          <p className="text-muted-foreground mt-1">
            Submit and track polygon-based scan requests
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isLocalDev && (
            <Button
              variant="outline"
              onClick={() => pullFromProdMutation.mutate()}
              disabled={pullFromProdMutation.isPending}
            >
              {pullFromProdMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Pull from production
            </Button>
          )}
          <Button onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Request
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {SCAN_REQUEST_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && (
          <span className="text-sm text-muted-foreground">
            {data.total} request{data.total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ListTodo className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No scan requests yet</h3>
            <p className="text-muted-foreground text-sm mt-1 mb-4">
              Create your first scan request to get started
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => {
            const vertexCount = getVertexCount(request.polygon);
            const requestedVars = request.requested_variables as Record<string, unknown> | null;
            const sportTypes = (requestedVars?.sportTypes as string[]) ?? [];

            return (
              <Card key={request.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <h3 className="text-sm font-medium truncate">
                        {(request as Record<string, unknown>).name as string ?? "Untitled"}
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className={STATUS_BADGE_STYLES[request.status as ScanRequestStatus]}
                        >
                          {request.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {vertexCount} vertices
                        </span>
                        {sportTypes.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {sportTypes.length} sport type{sportTypes.length !== 1 ? "s" : ""}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatRelativeDate(request.created_at)}
                        </span>
                      </div>
                      {request.notes && (
                        <p className="text-sm text-muted-foreground truncate">
                          {request.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isLocalDev &&
                        (request.status === "pending" || request.status === "approved") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setRunDialogRequest({
                                id: request.id,
                                name: (request as Record<string, unknown>).name as string | null ?? null,
                                polygon: request.polygon,
                                requestedVariables:
                                  (request.requested_variables as Record<string, unknown>) ?? null,
                              })
                            }
                          >
                            <Play className="mr-1.5 h-3.5 w-3.5" />
                            Run
                          </Button>
                        )}
                      {isLocalDev && (
                        <Select
                          value={request.status}
                          onValueChange={(newStatus) =>
                            updateStatusMutation.mutate({
                              id: request.id,
                              status: newStatus as ScanRequestStatus,
                            })
                          }
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SCAN_REQUEST_STATUSES.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {runDialogRequest && (
        <RunScanRequestDialog
          open={!!runDialogRequest}
          onOpenChange={(open) => {
            if (!open) setRunDialogRequest(null);
          }}
          requestId={runDialogRequest.id}
          requestName={runDialogRequest.name}
          polygon={runDialogRequest.polygon}
          requestedVariables={runDialogRequest.requestedVariables}
        />
      )}
    </div>
  );
}
